import { Link } from 'react-router-dom';
import { useState, useMemo, useCallback, type RefObject } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import { useAssignedStores } from '@/hooks/useAssignedStores';
import { useAnnouncementsInfinite, type AnnouncementFilters } from '@/hooks/useAnnouncementsInfinite';
import { useImeSafeSearch } from '@/hooks/useImeSafeSearch';
import { ActivityListSkeleton } from '@/components/ui/skeleton/activity-list-skeleton';
import { usePullToReload } from '@/hooks/usePullToReload';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { PullToRefreshIndicator } from '@/components/ui/PullToRefreshIndicator';
import { InfiniteScrollSentinel } from '@/components/shared/InfiniteScrollSentinel';
import { withMinDuration } from '@/lib/utils';
import { useHostessStore } from '@/stores/hostess';
import { CancelActivitySheet } from '@/components/app-hostesses/announcements/cancel-activity-sheet';
import { EditActivityDateSheet } from '@/components/app-hostesses/announcements/edit-activity-date-sheet';
import { AnnouncementsFilterDialog } from '@/components/app-hostesses/announcements/filter-dialog';
import { Switch } from '@sakura-grp/ui/switch';
import { StoreFilter } from '@/components/ui/store-filter';
import { ActivityRow, type ActivityRowData } from '@/components/app-hostesses/announcements/activity-row';
import type { DateRange } from '@sakura-grp/ui/calendar';
import type { StoreFilterItem } from '@/components/ui/store-filter';

interface EditingActivity {
  id: string;
  date: string;
  type: string;
  storeName?: string;
  customerName?: string;
}

interface EditingDateActivity {
  id: string;
  activityDateIso: string;
  type: string;
  storeName?: string;
  customerName?: string;
}

export default function Announcements() {
  const { hostess } = useHostessStore();
  const { data: assignedStores = [] } = useAssignedStores();

  // --- フィルタ state ---
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const search = useImeSafeSearch({onApply: setSearchQuery});
  const [editingActivity, setEditingActivity] = useState<EditingActivity | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingDateActivity, setEditingDateActivity] = useState<EditingDateActivity | null>(null);
  const [isEditDateDialogOpen, setIsEditDateDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const storesInitializedRef = useState(() => ({ current: false }))[0];

  // 所属店舗取得後に全店舗を選択（初回のみ）
  if (assignedStores.length > 0 && !storesInitializedRef.current) {
    storesInitializedRef.current = true;
    setSelectedStoreIds(assignedStores.map(s => s.id));
  }

  // StoreFilter 用のアイテム
  const storeFilterItems: StoreFilterItem[] = useMemo(
    () => assignedStores.map(s => ({ id: s.id, shortName: s.short_name })),
    [assignedStores]
  );
  const selectedStoreShortNames = useMemo(
    () => assignedStores
      .filter(s => selectedStoreIds.includes(s.id))
      .map(s => s.short_name),
    [assignedStores, selectedStoreIds]
  );

  const toggleStore = (shortName: string) => {
    const store = assignedStores.find(s => s.short_name === shortName);
    if (!store) return;
    setSelectedStoreIds(prev =>
      prev.includes(store.id)
        ? prev.filter(id => id !== store.id)
        : [...prev, store.id]
    );
  };

  // 全選択時はフィルタなし
  const storeIdsParam = useMemo(() => {
    if (selectedStoreIds.length === 0 || selectedStoreIds.length === assignedStores.length) {
      return null;
    }
    return selectedStoreIds;
  }, [selectedStoreIds, assignedStores]);

  // 日付フィルタ
  const dateFrom = dateRange?.from ? startOfDay(dateRange.from).toISOString() : null;
  const dateTo = dateRange?.to ? endOfDay(dateRange.to).toISOString() : null;

  const isFilterActive = dateRange !== undefined;

  const filters: AnnouncementFilters = {
    showOnlyMine,
    search: searchQuery,
    storeIds: storeIdsParam,
    dateFrom,
    dateTo,
  };

  // --- データ取得 ---
  const {
    data: pagesData,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAnnouncementsInfinite(filters);

  // 全ページの活動をフラットに結合
  const activities = useMemo(
    () => pagesData?.pages.flatMap(p => p.activities) ?? [],
    [pagesData]
  );

  // --- Pull to Refresh ---
  const { ref, refreshing, isReady } = usePullToReload({
    onRefresh: () => withMinDuration(async () => {
      await refetch();
    }),
    onError: (error) => {
      console.error("営業履歴データの更新に失敗しました:", error);
    }
  });
  useScrollRestoration(ref as RefObject<HTMLElement>);

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const handleActionClick = (activity: ActivityRowData) => {
    setEditingActivity({
      id: activity.id,
      date: activity.date,
      type: activity.type,
      storeName: activity.store,
      customerName: activity.customerName,
    });
    setIsEditDialogOpen(true);
  };

  const handleEditDateClick = (activity: ActivityRowData) => {
    setEditingDateActivity({
      id: activity.id,
      activityDateIso: activity.activityDateIso,
      type: activity.type,
      storeName: activity.store,
      customerName: activity.customerName,
    });
    setIsEditDateDialogOpen(true);
  };

  return (
    <main className="h-full flex flex-col">
      <div className="px-lg h-3xl flex items-center pb-[1.4rem] flex-shrink-0">
        <h1 className="text-3xl">営業履歴</h1>
      </div>
      <div
        ref={ref as any}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [touch-action:pan-y] [-webkit-overflow-scrolling:touch] border-t bg-white"
      >
        <div
          className="min-h-full will-change-transform"
          style={{
            transform: `translateY(var(--pull-offset, 0px))`,
            transition: 'var(--pull-transition, none)'
          }}
        >
          <PullToRefreshIndicator refreshing={refreshing} isReady={isReady} />

          {error && (
            <div className="px-lg py-md text-center text-red-500">
              営業履歴データの読み込みに失敗しました
            </div>
          )}

        {/* Filter Controls */}
        <div className="px-lg gap-md h-(--h-list) flex items-center border-b">
          <label className="space-y-sm grid cursor-pointer place-items-center leading-none">
            <Switch
              id="show-only-mine"
              checked={showOnlyMine}
              onCheckedChange={setShowOnlyMine}
              className="data-[state=checked]:bg-brand"
            />
            <span
              className={`text-2xs -mx-sm text-center font-bold tracking-tight ${
                showOnlyMine ? 'text-brand' : 'text-stone-400'
              }`}
            >
              自分のみ
            </span>
          </label>

          <input
            data-slot="input"
            className="h-xl bg-input/10 text-md shadow-xs selection:bg-primary selection:text-primary-foreground file:text-foreground placeholder:text-muted-foreground/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex min-w-0 flex-1 rounded-lg border px-2 py-1 outline-none transition-[color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            placeholder="名前・カナ・会社名で検索"
            {...search.inputProps}
          />

          <button
            data-slot="popover-trigger"
            className={`h-xl p-md shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex items-center justify-start whitespace-nowrap rounded-md border text-left text-xs font-normal tracking-tight outline-none transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 ${isFilterActive ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-background'}`}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={filterDialogOpen}
            onClick={() => setFilterDialogOpen(true)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />
            絞込
          </button>
          <AnnouncementsFilterDialog
            open={filterDialogOpen}
            onOpenChange={setFilterDialogOpen}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />

          <StoreFilter
            stores={storeFilterItems}
            selectedStores={selectedStoreShortNames}
            onToggle={toggleStore}
          />
        </div>

        {/* Activity List */}
        {isLoading ? (
          <ActivityListSkeleton />
        ) : (
          <ul className="divide-y divide-stone-200 border-b">
            {activities.length === 0 ? (
              <li className="px-lg py-xl text-center text-gray-500">
                該当する営業活動がありません
              </li>
            ) : activities.map((activity) => (
              <ActivityRow
                key={activity.id}
                activity={activity}
                onActionClick={handleActionClick}
                onEditDateClick={handleEditDateClick}
                customerCell={
                  <Link to={`/customers/${activity.customerId}`} className="space-y-sm">
                    <time className="block text-xs font-bold tracking-tight text-stone-500">
                      {activity.date}
                    </time>
                    <div className="flex items-center">
                      {activity.points !== undefined ? (
                        <div className="space-y-xs float-none w-[2.45rem] text-center text-[2rem]">
                          <div>{activity.customerType}</div>
                          <div className="text-sm text-brand">{activity.points}pt</div>
                        </div>
                      ) : (
                        <div className="float-none w-[2.45rem] text-center text-[2.45rem]">
                          {activity.customerType}
                        </div>
                      )}
                      <div className="space-y-xs leading-none">
                        <h3 className="text-xl">{activity.customerName}</h3>
                        {activity.company && (
                          <small className="mx-xs text-(length:--smaller-xl) line-clamp-1 tracking-normal">
                            ( {activity.company} )
                          </small>
                        )}
                      </div>
                    </div>
                  </Link>
                }
              />
            ))}
          </ul>
        )}

        {/* 無限スクロール sentinel */}
        <InfiniteScrollSentinel
          onLoadMore={handleLoadMore}
          hasNextPage={hasNextPage ?? false}
          isFetchingNextPage={isFetchingNextPage}
        />
        </div>
      </div>

      <CancelActivitySheet
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingActivity(null);
            refetch();
          }
        }}
        activity={editingActivity}
        hostessId={hostess?.id || ''}
      />

      <EditActivityDateSheet
        open={isEditDateDialogOpen}
        onOpenChange={(open) => {
          setIsEditDateDialogOpen(open);
          if (!open) {
            setEditingDateActivity(null);
            refetch();
          }
        }}
        activity={editingDateActivity}
      />
    </main>
  );
}
